import type { DbExecutor } from "./executor";
import type { AssemblyRepository, ProductPricingHook } from "./repositories/assemblyRepository";
import type { CatalogRepository } from "./repositories/catalogRepository";
import { computeAssembly, resolveDrivingQuantity, type DrivingQuantity } from "../engine/assembly";
import type { FeatureGeometryBinding, QuantityUnit } from "../domain/feature";
import type { MeasurementSource } from "../domain/measurement";
import { computeBom, type BomDemandInput, type BomResult } from "../engine/bom";
import type { ProjectProductSelection } from "../domain/catalog";

/**
 * BOM service (Phase 6): derives the Bill of Materials for a project from
 * the authoritative sources (features → assemblies → material components →
 * project product selections). Nothing is persisted. Also serves as the
 * assembly repository's ProductPricingHook so generated lines receive the
 * BOM's proportional, aggregate-then-round amounts.
 */
export class BomService implements ProductPricingHook {
  /** Active product-selection names per component of one feature (object inspector). */
  async selectionNamesForFeature(featureId: string): Promise<Map<string, string>> {
    const rows = await this.db.select<{ component_id: string; product_name_snapshot: string }>(
      `SELECT s.component_id, s.product_name_snapshot FROM project_product_selections s
         JOIN assembly_instance_components c ON c.id = s.component_id
         JOIN assembly_instances i ON i.id = c.instance_id
        WHERE i.project_feature_id = ? AND s.replaced_at IS NULL`,
      [featureId],
    );
    return new Map(rows.map((r) => [r.component_id, r.product_name_snapshot]));
  }

  constructor(
    private readonly db: DbExecutor,
    private readonly assemblies: AssemblyRepository,
    private readonly catalog: CatalogRepository,
  ) {}

  async applyTemplateDefault(componentId: string, productId: string): Promise<void> {
    const observations = await this.catalog.listObservations(productId);
    await this.catalog.selectProduct(componentId, productId, observations[0]?.id ?? null, {
      allowDiscontinued: false,
    });
  }

  async productDrivenAmounts(projectId: string): Promise<Map<string, { amountCents: number }>> {
    const bom = await this.computeForProject(projectId);
    const out = new Map<string, { amountCents: number }>();
    for (const g of bom.groups) {
      if (g.needsSelection) continue;
      for (const c of g.components) out.set(c.componentId, { amountCents: c.amountCents });
    }
    return out;
  }

  /** Demand inputs: every included material component of every active assembly. */
  /** The design a project's working estimate evaluates (Active Plan). */
  private async activeDesignId(projectId: string): Promise<string | null> {
    const rows = await this.db.select<{ active_design_id: string | null }>("SELECT active_design_id FROM projects WHERE id = ?", [projectId]);
    return rows[0]?.active_design_id ?? null;
  }

  async demandsForProject(projectId: string, designId?: string | null): Promise<BomDemandInput[]> {
    const evalDesign = designId === undefined ? await this.activeDesignId(projectId) : designId;
    const instances = await this.assemblies.listByProject(projectId);
    if (instances.length === 0) return [];
    const selections = await this.catalog.listSelectionsForProject(projectId);
    const byComponent = new Map<string, ProjectProductSelection>(selections.map((s) => [s.componentId, s]));
    const taxRows = await this.db.select<{ estimating_tax_pct_x100: number | null }>(
      "SELECT estimating_tax_pct_x100 FROM projects WHERE id = ?",
      [projectId],
    );
    const taxRate = taxRows[0]?.estimating_tax_pct_x100 ?? null;

    /*
     * THE FEATURES BEHIND THESE ASSEMBLIES, IN ONE QUERY (spec §67D).
     *
     * This loop used to fetch each feature by id. Against a local file that
     * is free; in the hosted build it was one HTTP round trip per assembly,
     * and the estimate screen runs this more than once — 177 requests on a
     * real project, measured on the live beta.
     */
    interface FeatureRow {
      id: string;
      name: string;
      room_id: string | null;
      floor_id: string | null;
      quantity_x100: number | null;
      quantity_unit: string | null;
      quantity_source: string | null;
      quantity_geometry_binding: string | null;
      quantity_geometry_x100: number | null;
      design_id: string | null;
    }
    const featureIds = [...new Set(instances.map((i) => i.projectFeatureId))];
    const featureRows =
      featureIds.length === 0
        ? []
        : await this.db.select<FeatureRow>(
            `SELECT id, name, room_id, floor_id, quantity_x100, quantity_unit, quantity_source,
                    quantity_geometry_binding, quantity_geometry_x100, design_id
               FROM project_features WHERE id IN (${featureIds.map(() => "?").join(", ")})`,
            featureIds,
          );
    const featureById = new Map(featureRows.map((row) => [row.id, row]));

    // The label each demand is filed under ("Floor 1 → Kitchen"). Two more
    // queries for the whole project instead of one per feature (§67D).
    const roomIds = [...new Set(featureRows.map((r) => r.room_id).filter((id): id is string => id !== null))];
    const floorIds = [...new Set(featureRows.map((r) => r.floor_id).filter((id): id is string => id !== null))];
    const roomLabels = new Map<string, string>();
    if (roomIds.length > 0) {
      const rows = await this.db.select<{ id: string; name: string; floor_name: string }>(
        `SELECT r.id, r.name, fl.name AS floor_name FROM rooms r
           JOIN floors fl ON fl.id = r.floor_id
          WHERE r.id IN (${roomIds.map(() => "?").join(", ")})`,
        roomIds,
      );
      for (const row of rows) roomLabels.set(row.id, `${row.floor_name} → ${row.name}`);
    }
    const floorLabels = new Map<string, string>();
    if (floorIds.length > 0) {
      const rows = await this.db.select<{ id: string; name: string }>(
        `SELECT id, name FROM floors WHERE id IN (${floorIds.map(() => "?").join(", ")})`,
        floorIds,
      );
      for (const row of rows) floorLabels.set(row.id, row.name);
    }

    const demands: BomDemandInput[] = [];
    for (const instance of instances) {
      const f = featureById.get(instance.projectFeatureId);
      if (!f) continue;
      // Scenario filter: common features (NULL) + the evaluated design's.
      if (f.design_id !== null && f.design_id !== evalDesign) continue;
      let scope = "Project";
      if (f.room_id) scope = roomLabels.get(f.room_id) ?? scope;
      else if (f.floor_id) scope = floorLabels.get(f.floor_id) ?? scope;
      // Driving quantity: ONE shared rule (engine resolveDrivingQuantity).
      const resolved = resolveDrivingQuantity(
        {
          quantityX100: f.quantity_x100 === null ? null : Number(f.quantity_x100),
          quantityUnit: f.quantity_unit as QuantityUnit | null,
          quantitySource: f.quantity_source as MeasurementSource | null,
          quantityGeometryBinding: (f.quantity_geometry_binding as FeatureGeometryBinding | null) ?? null,
          quantityGeometryX100: f.quantity_geometry_x100 === null || f.quantity_geometry_x100 === undefined ? null : Number(f.quantity_geometry_x100),
        },
        instance.drivingUnit,
      );
      if (!resolved.ok) continue; // unresolvable; assembly panel surfaces this
      const driving: DrivingQuantity = resolved.driving;
      const calc = computeAssembly(instance, driving, taxRate);
      for (const c of calc.components) {
        const component = instance.components.find((x) => x.id === c.componentId);
        if (!component || !c.included || component.componentType !== "material") continue;
        demands.push({
          componentId: component.id,
          componentName: component.name,
          instanceId: instance.id,
          featureId: f.id,
          featureName: f.name,
          scopeLabel: scope,
          requiredX100: c.effectiveQtyX100,
          unit: component.unit,
          taxable: component.taxable,
          estimatingAmountCents: c.amountCents,
          selection: byComponent.get(component.id) ?? null,
        });
      }
    }
    return demands;
  }

  async computeForDesign(projectId: string, designId: string | null): Promise<BomResult> {
    const demands = await this.demandsForProject(projectId, designId);
    const taxRows = await this.db.select<{ estimating_tax_pct_x100: number | null }>("SELECT estimating_tax_pct_x100 FROM projects WHERE id = ?", [projectId]);
    return computeBom(demands, taxRows[0]?.estimating_tax_pct_x100 ?? null);
  }

  async computeForProject(projectId: string): Promise<BomResult> {
    const taxRows = await this.db.select<{ estimating_tax_pct_x100: number | null }>(
      "SELECT estimating_tax_pct_x100 FROM projects WHERE id = ?",
      [projectId],
    );
    return computeBom(await this.demandsForProject(projectId), taxRows[0]?.estimating_tax_pct_x100 ?? null);
  }

  /** Select a product for a component and regenerate the project's lines. */
  async selectProduct(componentId: string, productId: string, priceObservationId: string | null, options: { allowDiscontinued?: boolean } = {}): Promise<void> {
    await this.catalog.selectProduct(componentId, productId, priceObservationId, options);
    await this.regenerateOwner(componentId);
  }

  async clearSelection(componentId: string): Promise<void> {
    await this.catalog.clearSelection(componentId);
    await this.regenerateOwner(componentId);
  }

  async updateSelectionFromCatalog(componentId: string, priceObservationId: string | null): Promise<void> {
    await this.catalog.updateSelectionFromCatalog(componentId, priceObservationId);
    await this.regenerateOwner(componentId);
  }

  async updateSelectionSettings(componentId: string, selectionId: string, settings: { roundSeparately?: boolean; purchaseQtyOverride?: number | null }): Promise<void> {
    await this.catalog.updateSelectionSettings(selectionId, settings);
    await this.regenerateOwner(componentId);
  }

  private async regenerateOwner(componentId: string): Promise<void> {
    const projectId = await this.assemblies.projectIdForComponent(componentId);
    if (projectId) await this.assemblies.regenerateForProject(projectId);
  }
}
